import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import EventEmitter from "../../../services/event-emitter";
import NumberFormat from "react-number-format";
import AbbreviateNumber from "../../../components/ui-building-blocks/numbers/AbbreviateNumber";
import { batchRequest, postRequest, putRequest } from "../../../api/ApiService";
import { logError } from "../../../services/utils";
import _ from "lodash";
import { Loader } from "react-loaders";
import {
  incrementUnacceptedDecisions,
  decrementUnacceptedDecisions
} from "../../../api/SubmitDecisions";
import SuccessAlert from "../../alerts/success";
import Fragment from "react-dot-fragment";
import styled from 'styled-components';

import Auth from "../../../routes/Auth";
import ErrorAlert from '../../alerts/error';

import IconTableHelp from "../../../containers/assets/icon-table-help.svg";

import LoanRepaymentVideo from '../../../containers/assets/finance-loan-repayment.mp4';

const DEBT_CLIENTS_URL = `${process.env.REACT_APP_API_URL}static/debt_clients/`;
const DEBTS_URL = `${process.env.REACT_APP_API_URL}team/debts/`;
const DEBT_REPAYMENTS_URL = `${process.env.REACT_APP_API_URL}team/debt_repayments/`;
const FINANCE_INTEREST_PAYMENTS_URL = `${process.env.REACT_APP_API_URL}v2/finance/interest-payments/`;
const PLAYERS_URL = `${process.env.REACT_APP_API_URL}team/players/`;

const HelpIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 4px;
  cursor: pointer;
`;

const ModalVideoWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3A507B;
  border-radius: 10px;

  &:before {
    display: block;
    content: '';
    align-items: center;
    background: #0006;
    display: flex;
    justify-content: center;
    opacity: 1;
    position: absolute;
    left: 0px;
    right: 0px;
    top: 0px;
    bottom: 0px;
    transition: opacity .3s ease;
    border-radius: 10px;
  }

  &.is-playing:before, &.is-playing button {
    opacity: 0;
  }
  &.is-playing:hover:before, &.is-playing:hover button {
    opacity: 1;
  }
  &:hover video {
    cursor: pointer;
  }
`;

const PlayPauseButton = styled.button`
  align-items: center;
  background: none;
  background: #2a4169;
  border: none;
  border-radius: 50%;
  box-shadow: 0 4px 4px 0 #0000001a;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-size: 2rem;
  height: 80px;
  justify-content: center;
  padding: 0;
  text-align: center;
  width: 80px;
  z-index: 2;
  position: absolute;
  transition: opacity .3s ease;
  opacity: 1;
`;

const CloseModalVideoButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 20px;
  color: #fff;
  padding: 0;
  line-height: normal;
`;

class ReturnDebtModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      show: false,
      clients: [],
      loading: true,
      saving: {},
      successMessage: null,
      interestRepaymentSaving: {},
      playerId: 0,
      showError: false,
      errorTitle: '',
      errorMessage: '',
      isVideoModalShown: false,
      isVideoPlaying: false
    };

    this.eventemitter = EventEmitter.getInstance();

    this.videoRef = null;
  }

  handlePlayPause = () => {
    if (this.videoRef) {
      if (this.state.isVideoPlaying) {
        this.videoRef.pause();
      } else {
        this.videoRef.play();
      }
      this.setState({ isVideoPlaying: !this.state.isVideoPlaying });
    }
  };

  openVideoModal = () => {
    this.setState({
      isVideoModalShown: true
    });
  };

  closeVideoModal = () => {
    this.setState({
      isVideoModalShown: false,
      isVideoPlaying: false
    });
  };

  showSuccessMessage = successMessage => {
    this.setState({
      successMessage,
    }, () => {
      this.hideSuccessMessage();
    });
  }

  hideSuccessMessage = () => {
    setTimeout(() => {
      this.setState({
        successMessage: null,
      });
    }, 5000);
  }

  showErrorMessage = errorMessage => {
    this.setState({
      errorMessage,
    }, () => {
      this.hideErrorMessage();
    });
  }

  hideErrorMessage = () => {
    setTimeout(() => {
      this.setState({
        errorMessage: null,
      });
    }, 5000);
  }

  fetchData = () => {
    const { roundNumber } = this.props;

    batchRequest([
      DEBT_CLIENTS_URL + `?round_number=${roundNumber}`,
      DEBTS_URL + `?round_number=${roundNumber}`,
      DEBT_REPAYMENTS_URL + `?round_number=${roundNumber}`,
      DEBTS_URL + `?round_number=${roundNumber - 1}`,
      FINANCE_INTEREST_PAYMENTS_URL + `?round_number=${roundNumber}`,
      PLAYERS_URL
    ])
      .then(res => {
        if (
          res[0].statusCode !== 200 ||
          res[1].statusCode !== 200 ||
          res[2].statusCode !== 200 ||
          res[3].statusCode !== 200 ||
          res[4].statusCode !== 200 ||
          res[5].statusCode !== 200
        ) {
          throw res;
        }

        const debt_clients = res[0].body;
        const debts = res[1].body;
        const debt_repayments = res[2].body;
        const debts_prior = res[3].body;
        const finance_iterest_payments = res[4].body.results;
        const players = res[5].body;

        const userId = Number(Auth.getUserId());
        const player = players.find(i => Number(i.user.id) === userId);
        const playerId = player ? Number(player.id) : 0;

        let clients = debt_clients.map(client => {
          const client_id = parseInt(client.id, 10);
          const { name } = client;
          const term = client_id === 2 || client_id === 3 ? 5 : 1;
          const debt = debts.find(i => i.client === client_id);
          const debt_prior = debts_prior.find(i => i.client === client_id);
          const debt_repayment = debt_repayments.find(
            i => i.client === client_id
          );
          const finance_interest_payment = finance_iterest_payments.find(
            i => i.client === client_id
          );

          const interest_beginning_balance = debt_prior
            ? Math.abs(debt_prior.interest_payable)
            : 0;

          return {
            client_id,
            name,
            term: debt ? debt.term : term,
            balance: debt ? debt.balance : 0,
            debt_id: debt ? debt.id : 0,
            interest_payable: debt ? debt.interest_payable : 0,
            interest_charge: debt ? debt.interest_charge : 0,
            prior_balance: debt_prior ? debt_prior.balance : 0,
            amount_repaid: debt_repayment ? debt_repayment.amount : 0,
            repayment_amount: debt_repayment ? debt_repayment.amount : 0,
            repayment_debt_id: debt_repayment ? debt_repayment.id : 0,
            interest_beginning_balance,
            interest_repayment: finance_interest_payment ? finance_interest_payment.amount : 0,
            interest_repayment_id: finance_interest_payment ? finance_interest_payment.id : 0,
          };
        });

        this.setState({
          clients,
          loading: false,
          playerId
        });
      })
      .catch(err => {
        logError(err, "ReturnDebtModal", "fetchData");
      });
  };

  getSavingState = client_id => {
    const { saving } = this.state;

    if (client_id in saving) {
      return saving[client_id];
    }

    return false;
  };

  setSavingState = (client_id, value) => {
    let saving = _.cloneDeep(this.state.saving);
    saving[client_id] = value;

    this.setState({
      saving
    });
  };

  getInterestRepaymentSavingState = client_id => {
    const { interestRepaymentSaving } = this.state;

    if (client_id in interestRepaymentSaving) {
      return interestRepaymentSaving[client_id];
    }

    return false;
  };

  setInterestRepaymentSavingState = (client_id, value) => {
    let interestRepaymentSaving = _.cloneDeep(this.state.interestRepaymentSaving);
    interestRepaymentSaving[client_id] = value;

    this.setState({
      interestRepaymentSaving
    });
  };

  close = () => {
    this.setState({
      show: false
    });
  };

  show = () => {
    this.setState(
      {
        show: true
      },
      () => {
        this.fetchData();
      }
    );
  };

  componentDidMount() {
    this.eventemitter.on("show-return-debt-modal", this.show);
  }

  componentWillUnmount() {
    this.eventemitter.off("show-return-debt-modal", this.show);
  }

  changeRepaymentAmount = (e, value, client_id) => {
    let clients = _.cloneDeep(this.state.clients);
    const val =
      "floatValue" in value ? value.floatValue : Number(e.target.value);
    const index = clients.findIndex(i => i.client_id === client_id);

    if (index >= 0) {
      clients[index].repayment_amount = val;

      this.setState({
        clients
      });
    }
  };

  changeInterestRepaymentsAmount = (e, value, client_id) => {
    let clients = _.cloneDeep(this.state.clients);
    const val =
      "floatValue" in value ? value.floatValue : Number(e.target.value);
    const index = clients.findIndex(i => i.client_id === client_id);

    if (index >= 0) {
      clients[index].interest_repayment = val;

      this.setState({
        clients
      });
    }
  };

  getDebtsUrl = object_id => {
    const url = `${process.env.REACT_APP_API_URL}team/debt_repayments/`;

    return object_id > 0 ? putRequest(`${url}${object_id}/`) : postRequest(url);
  };

  getInterestRepaymentUrl = object_id => {
    const url = FINANCE_INTEREST_PAYMENTS_URL;

    return object_id > 0 ? putRequest(`${url}${object_id}/`) : postRequest(url);
  };

  submit = client_id => {
    let errorTitle = '';
    let errorMessage = '';

    const { reloadData } = this.props;
    const { clients } = this.state;
    const client = clients.find(i => i.client_id === client_id);

    if (!client) {
      return;
    }

    if (client.prior_balance <= 0) {
      errorTitle = 'No Prior Year Outstanding Balance Exist';
      errorMessage = 'You do not have any outstanding loan balance with this lender';
    }

    if (client.repayment_amount < 0) {
      errorTitle = 'Value Cannot Be Negative';
      errorMessage = 'Please input a value that’s zero or greater';
    }

    if (errorMessage !== '') {
      this.setState({
        showError: true,
        errorTitle,
        errorMessage
      });
      return;
    }

    this.setSavingState(client_id, true);

    this.getDebtsUrl(client.repayment_debt_id)
      .send({
        client: client_id,
        term: client.term,
        amount: client.repayment_amount
      })
      .then(res => {
        if (client.repayment_amount === 0) {
          decrementUnacceptedDecisions("finance");
        } else {
          incrementUnacceptedDecisions("finance");
        }
        this.showSuccessMessage((
          <Fragment>
            <b>{(client.repayment_amount).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })}</b> is successfully scheduled to be paid to <b>{client.name}</b>
          </Fragment>
        ));
        this.setSavingState(client_id, false);
        reloadData();
        this.fetchData();
      })
      .catch(err => {
        if (
          err.response.status === 400 &&
          err.response.body.non_field_errors &&
          err.response.body.non_field_errors.length > 0
        ) {
          const error = err.response.body.non_field_errors[0];

          if (
            error.length >= 25 &&
            error.substr(0, 25) === "Insufficient cash balance"
          ) {
            errorTitle = 'Value > Available Cash';
            errorMessage = 'Payment cannot exceed your available cash balance';
          }

          if (
            error.length >= 24 &&
            error.substr(0, 24) === "paying more than you owe"
          ) {
            errorTitle = 'Payment Exceeds Outstanding Balance';
            errorMessage = 'Your payment request is in excess of what you owe. Please adjust payment amount accordingly.';
          }

          if (error === "Manual repayments not allowed for short term debts") {
            errorTitle = 'Debt to Capital Breach';
            errorMessage = 'This payment would put you in breach of your lender(s) debt-to-capital limit. In order to make this payment, you’ll need to reduce your debt-to-capital ratio.';
          }
          this.setSavingState(client_id, false);

          if (errorMessage !== '') {
            this.setState({
              showError: true,
              errorTitle,
              errorMessage
            });
          }
        }

        logError(err, "ReturnDebtModal", "submit");
      });
  };

  submitInterestRepayment = client_id => {
    const { reloadData, roundId } = this.props;
    const { clients, playerId } = this.state;
    const client = clients.find(i => i.client_id === client_id);

    if (!client) {
      return;
    }

    this.setInterestRepaymentSavingState(client_id, true);

    this.getInterestRepaymentUrl(client.interest_repayment_id)
      .send({
        client: client_id,
        amount: client.interest_repayment,
        debt: client.debt_id,
        player: playerId,
        round: roundId,
        team: Auth.getTeamId(),
        term: client.term
      })
      .then(res => {
        this.showSuccessMessage((
          <Fragment>
            <b>{(client.interest_repayment).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })}</b> is successfully set to be paid to <b>{client.name}</b>
          </Fragment>
        ));
        this.setInterestRepaymentSavingState(client_id, false);
        reloadData();
        this.fetchData();
      })
      .catch(err => {
        if (
          err.response.status === 400 &&
          err.response.body.non_field_errors &&
          err.response.body.non_field_errors.length > 0
        ) {
          const error = err.response.body.non_field_errors[0];
          let errorTitle = '';
          let errorMessage = '';

          if (error.indexOf("Amount can not exceed interest_payable") !== -1) {
            errorTitle = 'Payment Exceeds Outstanding Balance';
            errorMessage = 'Your payment request is in excess of what you owe. Please adjust payment amount accordingly.';
          }

          this.setInterestRepaymentSavingState(client_id, false);

          if (errorMessage !== '') {
            this.setState({
              showError: true,
              errorTitle,
              errorMessage
            });
          }
        }
        logError(err, "ReturnDebtModal", "submit");
      });
  };

  get cashBalance() {
    const { cashBalance } = this.props;

    return Number(cashBalance).toFixed(0);
  }

  hideError = () => {
    this.setState({
      showError: false
    });
  };

  render() {
    const {
      show,
      clients,
      loading,
      successMessage,
      showError,
      errorTitle,
      errorMessage,
      isVideoModalShown,
      isVideoPlaying
    } = this.state;
    const cashBalance = this.cashBalance;

    return (
      <Fragment>
        <Modal
          show={show}
          onHide={this.close}
          dialogClassName="modal-dialog-bigger-more fn-modal-dialog finance-decisions-modal"
        >
          {showError && (
            <ErrorAlert
              title={errorTitle}
              message={errorMessage}
              onHide={this.hideError}
            />
          )}
          <Modal.Header closeButton={true}>
            <h3>
              Returning Capital | Paying Down Debt
              <HelpIcon src={IconTableHelp} onClick={this.openVideoModal} />
            </h3>
            <p>Cash Balance: <NumberFormat
              value={cashBalance}
              displayType="text"
              prefix="$"
              thousandSeparator=","
            />
            </p>
          </Modal.Header>
          {loading === true && (
            <Modal.Body>
              <div className="text-center loader-inline">
                <Loader type="ball-scale-ripple" active />
              </div>
            </Modal.Body>
          )}
          {loading === false && (
            <Modal.Body>
              {successMessage && <SuccessAlert
                message={successMessage}
                marginTop={1}
                marginBottom={80}
              />}
              <div className="modal-line loan-line">
                <p className="modal-label grey">Loan Principal</p>
                <div className="modal-right-content-container content-container-row col-3">
                  <p className="modal-label black">Prudent Bank</p>
                  <p className="modal-label black">Mezz Capital</p>
                  <p className="modal-label black">Great White Financials</p>
                </div>
              </div>
              <div className="modal-line">
                <p className="modal-label">Loan Beginning Balance</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-label" key={client.client_id}>
                      <NumberFormat
                        value={client.prior_balance}
                        displayType="text"
                        prefix="$"
                        thousandSeparator=","
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-line principal-repayment-line">
                <p className="modal-label black bigger">Principal Repayment(s)</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-input-wrapper" key={client.client_id}>
                      <AbbreviateNumber
                        type="input"
                        value={client.repayment_amount}
                        onChange={(e, value) =>
                          this.changeRepaymentAmount(
                            e,
                            value,
                            client.client_id
                          )
                        }
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-line">
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => {
                    const saving = this.getSavingState(client.client_id);

                    return (
                      <div key={client.client_id}>
                        <button
                          type="button"
                          className="modal-button"
                          onClick={() => this.submit(client.client_id)}
                          disabled={saving}
                        >
                          {saving === true ? "Loading..." : "Save"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-line loan-ending-balance-line">
                <p className="modal-label">Loan Ending Balance</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-label" key={client.client_id}>
                      <NumberFormat
                        value={client.balance}
                        displayType="text"
                        prefix="$"
                        thousandSeparator=","
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-divider"></div>
              <div className="modal-line loan-line loan-interest-line">
                <p className="modal-label grey">Loan Interest</p>
              </div>
              <div className="modal-line">
                <p className="modal-label">Interest Beginning Balance</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-label" key={client.client_id}>
                      <NumberFormat
                        value={client.interest_beginning_balance}
                        displayType="text"
                        prefix="$"
                        thousandSeparator=","
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-line interest-repayment-line">
                <p className="modal-label black bigger">Interest Repayment(s)</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-input-wrapper" key={client.client_id}>
                      <AbbreviateNumber
                        type="input"
                        value={client.interest_repayment}
                        onChange={(e, value) =>
                          this.changeInterestRepaymentsAmount(
                            e,
                            value,
                            client.client_id
                          )
                        }
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-line interest-repayment-buttons-line">
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => {
                    const interestRepaymentSaving = this.getInterestRepaymentSavingState(client.client_id);

                    return (
                      <div key={client.client_id}>
                        <button
                          type="button"
                          className="modal-button"
                          onClick={() => this.submitInterestRepayment(client.client_id)}
                          disabled={interestRepaymentSaving}
                        >
                          {interestRepaymentSaving === true ? "Loading..." : "Save"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-line">
                <p className="modal-label">Current Year Interest</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-label" key={client.client_id}>
                      <NumberFormat
                        value={client.interest_charge}
                        displayType="text"
                        prefix="$"
                        thousandSeparator=","
                      />
                    </p>
                  ))}
                </div>
              </div>
              <div className="modal-line">
                <p className="modal-label">Interest Ending Balance</p>
                <div className="modal-right-content-container content-container-row col-3">
                  {clients.map(client => (
                    <p className="modal-label" key={client.client_id}>
                      <NumberFormat
                        value={client.interest_payable}
                        displayType="text"
                        prefix="$"
                        thousandSeparator=","
                      />
                    </p>
                  ))}
                </div>
              </div>
            </Modal.Body>
          )}
        </Modal>
        <Modal show={isVideoModalShown} onHide={this.closeVideoModal} className="goals-and-targets-modal">
          <Modal.Body>
            <ModalVideoWrapper className={isVideoPlaying ? 'is-playing' : ''}>
              <video
                ref={(ref) => { this.videoRef = ref; }}
                src={LoanRepaymentVideo}
                alt="Welcome Video"
                onClick={this.handlePlayPause}
                onEnded={() => this.setState({ isVideoPlaying: false })}
                style={{ width: '100%', borderRadius: 10 }}
              />
              <PlayPauseButton onClick={this.handlePlayPause}>
                {isVideoPlaying ? '❚❚' : '▶'}
              </PlayPauseButton>
              <CloseModalVideoButton onClick={this.closeVideoModal}>×</CloseModalVideoButton>
            </ModalVideoWrapper>
          </Modal.Body>
        </Modal>
      </Fragment>
    );
  }
}

export default ReturnDebtModal;



// WEBPACK FOOTER //
// ./src/components/finance/decisions/return-debt-modal.js